/* eslint-disable camelcase, jsx-a11y/control-has-associated-label */
import styled from 'styled-components';
import { Icon, useThemeColor, Tooltip, Link, datetimeHelpers, Checkbox, Text } from '@wistia/vhs';
import { logMediaEvent } from '~/utilities/logEvent';
import { noOpFn } from '~/utilities/util';
import { trackStructEvent } from '~/utilities/snowplow';
import type { Nilable } from '~/utilities/type-guards';
import { isNonEmptyArray, isNotNil } from '~/utilities/type-guards';
import type { JSX } from 'react';
import { useState } from 'react';
import type { MediaType } from '~/graphql';
import { useBulkDownloadRecordStreamsMutation } from '~/singlePageApp/screens/Media/MediaSidePanel/__gql/BulkDownloadRecordStreams.gql.gen';
import {
  useNotificationStore,
  getNotificationStoreActions,
} from '~/components/NotificationTray/NotificationStore';
import { addDownloadActionToNotificationTray } from '~/singlePageApp/components/BulkActionBar/shared/helpers';

const { mediaDurationString } = datetimeHelpers;

const Cell = styled.div`
  color: ${({ theme }) => theme.color.grey900};
  font-size: 14px;
  line-height: 20px;
  margin: 0 16px 16px 0;
`;

const IconCell = styled.div`
  margin-bottom: 16px;
`;

const TableWrapper = styled.table`
  width: 100%;
`;

const Hint = styled.div`
  padding: ${({ theme }) => theme.spacing.space02};
`;

export interface DownloadableMediaFile {
  id: string;
  displayName: string;
  extension?: string;
  width: number;
  height: number;
  length: number;
  formattedFilesize: string;
  downloadUrl: string;
  captionedDownloadUrl: Nilable<string>;
  createdAt?: string;
  mediaHashedId?: string;
}

export interface TimeCodedTranscript {
  id: string;
  hashedId: string;
  englishName: string;
  activeVersion: number;
  ietfLanguageTag: string;
}

interface DownloadsTableParams {
  downloadableMediaFiles: DownloadableMediaFile[];
  mediaType: MediaType;
  mediaHashedId: string;
  mediaId: Nilable<string>;
  onDownloadClick: () => void;
  replacedMediaFile?: DownloadableMediaFile;
  transcripts: TimeCodedTranscript[];
  hasIsos?: boolean;
  hideModal: () => unknown;
}

const generateTranscriptParams = ({
  transcripts,
  mediaHashedId,
}: {
  transcripts: TimeCodedTranscript[];
  mediaHashedId: string;
}): string => {
  const transcriptParams = transcripts
    .map(
      ({ hashedId, ietfLanguageTag, activeVersion }) =>
        `transcript=${hashedId}-${ietfLanguageTag}-${activeVersion}`,
    )
    .join('&');

  return `&media_id=${mediaHashedId}&${transcriptParams}`;
};

const generateTracksList = (transcripts: TimeCodedTranscript[]): string => {
  return transcripts.map(({ englishName }) => englishName).join(', ');
};

const useDownloadIsos = () => {
  const [downloadStreams] = useBulkDownloadRecordStreamsMutation();
  const { addTaskToQueue } = useNotificationStore(getNotificationStoreActions);

  return async (mediaId: string) => {
    const result = await downloadStreams({
      variables: {
        input: {
          mediaId,
        },
      },
    });
    const jobId = result.data?.bulkDownloadRecordStreams?.backgroundJobStatus.id;

    if (isNotNil(jobId)) {
      addDownloadActionToNotificationTray({
        addTaskToQueue,
        backgroundJobStatusId: jobId,
        /**
         * We're downloading the individual streams for one media, so we provide a selection count of 1
         */
        selectionCount: 1,
      });
    }
  };
};

export const DownloadsTable = ({
  downloadableMediaFiles,
  mediaType,
  mediaHashedId,
  mediaId,
  onDownloadClick = noOpFn,
  replacedMediaFile,
  transcripts,
  hasIsos = false,
  hideModal,
}: DownloadsTableParams): JSX.Element => {
  const captionedDownloadsAvailable = isNonEmptyArray(transcripts) && mediaType === 'Video';
  const [captionsSelected, setCaptionsSelected] = useState(captionedDownloadsAvailable);
  const downloadIsos = useDownloadIsos();

  const transcriptParams = generateTranscriptParams({
    transcripts,
    mediaHashedId,
  });
  const availableCaptionsTracks = generateTracksList(transcripts);

  const filename = (name: string): string => {
    switch (name) {
      case 'Thumbnail Image':
        return 'Episode Art / Thumbnail';
      case 'mp3 encode':
        return 'Encoded Version';
      default:
        return name;
    }
  };

  const onClick = (displayName: string, fileIsCaptionable: boolean) => {
    if (process.env.NODE_ENV !== 'test') {
      void logMediaEvent({ action: 'download', hashedId: mediaHashedId, displayName });

      trackStructEvent({
        category: 'link',
        action: 'click',
        label: 'download-modal',
        property: 'download-type',
        value: JSON.stringify({
          file_type: displayName,
          download_with_captions: captionsSelected && fileIsCaptionable,
        }),
      });
    }

    onDownloadClick();
  };

  const grey700 = useThemeColor('grey700');
  const mediaFiles = isNotNil(replacedMediaFile)
    ? [...downloadableMediaFiles, replacedMediaFile]
    : downloadableMediaFiles;

  return (
    <>
      <TableWrapper>
        <thead />
        <tbody>
          {mediaFiles.map(
            ({
              id,
              displayName,
              extension,
              width,
              height,
              length,
              formattedFilesize,
              downloadUrl,
              captionedDownloadUrl,
            }) => (
              <tr key={id}>
                <td>
                  <Cell>{filename(displayName)}</Cell>
                </td>
                <td>
                  <Cell>{extension ?? '--'}</Cell>
                </td>
                <td align="center">
                  {mediaType === 'Audio' && length ? (
                    <Cell>{mediaDurationString(length * 1000)}</Cell>
                  ) : (
                    <Cell>
                      {width} x {height}
                    </Cell>
                  )}
                </td>
                <td>
                  <Cell>{formattedFilesize}</Cell>
                </td>
                <td align="right">
                  <IconCell>
                    <Link
                      beforeAction={() => {
                        onClick(filename(displayName), isNotNil(captionedDownloadUrl));
                      }}
                      data-testid="media-file-download-link"
                      href={
                        captionsSelected && isNotNil(captionedDownloadUrl)
                          ? `${captionedDownloadUrl}${transcriptParams}`
                          : downloadUrl
                      }
                      onContextMenu={() => {
                        onClick(filename(displayName), isNotNil(captionedDownloadUrl));
                      }}
                    >
                      <Tooltip label="Download Asset">
                        <Icon
                          color={grey700}
                          size="md"
                          type="download"
                        />
                      </Tooltip>
                    </Link>
                  </IconCell>
                </td>
              </tr>
            ),
          )}
          {hasIsos && isNotNil(mediaId) && (
            <tr>
              <td>
                <Cell>Isolated feeds</Cell>
              </td>
              <td>
                <Cell>zip</Cell>
              </td>
              <td align="center" />
              <td />
              <td align="right">
                <IconCell>
                  {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                  <Link
                    beforeAction={() => {
                      if (process.env.NODE_ENV !== 'test') {
                        void logMediaEvent({
                          action: 'download',
                          hashedId: mediaHashedId,
                          displayName: 'Isolated files',
                        });

                        trackStructEvent({
                          category: 'link',
                          action: 'click',
                          label: 'download-modal',
                          property: 'download-type',
                          value: JSON.stringify({
                            file_type: 'Isolated files',
                            download_with_captions: false,
                          }),
                        });
                      }
                      void downloadIsos(mediaId);
                      hideModal();
                      onDownloadClick();
                    }}
                    data-testid="media-isolate-files-download"
                    href="#"
                  >
                    <Tooltip label="Download Isolated Files">
                      <Icon
                        color={grey700}
                        size="md"
                        type="download"
                      />
                    </Tooltip>
                  </Link>
                </IconCell>
              </td>
            </tr>
          )}
        </tbody>
      </TableWrapper>
      {captionedDownloadsAvailable ? (
        <>
          <Checkbox
            label={`Download with captions (not available for uncompressed files${hasIsos ? ' or isolated feeds' : ''})`}
            checked={captionsSelected}
            onChange={() => setCaptionsSelected(!captionsSelected)}
          />
          {captionsSelected ? (
            <Hint>
              <Text
                variant="body2"
                colorOverride={grey700}
              >
                Included caption tracks: {availableCaptionsTracks}
              </Text>
            </Hint>
          ) : null}
        </>
      ) : null}
    </>
  );
};
